/* RESPONSIBLE TEAM: team-ml */
import Step, {
  type CreateStepParams,
  IsCustomerFacing,
} from 'embercom/models/operator/visual-builder/step';
import type Store from '@ember-data/store';
import { attr, hasMany, type SyncHasMany } from '@ember-data/model';
import type { Block } from 'embercom/models/common/blocks/block';
import type AttributeDescriptor from 'embercom/models/operator/visual-builder/attribute-descriptor';
import { LOCAL_VARIABLE_IDENTIFIER_PREFIX } from 'embercom/models/operator/visual-builder/attribute-descriptor';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import { buildValidations, validator } from 'ember-cp-validations';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { SUPPORTED_CHANNELS } from 'embercom/lib/operator/custom-bots/constants-2';
import { TargetContext } from 'embercom/objects/operator/configuration/custom-bot';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { computed } from '@ember/object';
// eslint-disable-next-line ember/no-computed-properties-in-native-classes
import { equal, reads } from '@ember/object/computed';
import { extractTemplateVariables } from 'embercom/lib/workflows/template-variables';
import type ConnectionPoint from '../connection-point';
import type FinCustomProcedureContent from './fin-custom/procedure-content';
import generateUUID from 'embercom/lib/uuid-generator';
// eslint-disable-next-line no-restricted-imports
import { getApp } from 'embercom/lib/container-lookup';
import type { BlockList } from '@intercom/interblocks.ts';
import { tracked } from '@glimmer/tracking';

export const finCustomStepBlockValidators = {
  blocks: [
    validator('blocks-not-empty', {
      message: 'operator.workflows.visual-builder.validations.fin-custom.blank-prompt-error',
      messageTranslated: true,
      disabled: equal('model.isV3', true),
    }),
  ],
  finOutputParams: [
    validator('presence', {
      presence: true,
      messageKey: 'operator.workflows.visual-builder.validations.fin-custom.no-output-params',
      disabled: equal('model.isV3', true),
    }),
    validator('inline', {
      validate(value: AttributeDescriptor[], _options: any, model: FinCustom) {
        let invalidParams = value.filter(
          (param) => !param.name?.trim() || !param.description?.trim(),
        );
        if (invalidParams.length > 0) {
          return model.intl.t(
            'operator.workflows.visual-builder.validations.fin-custom.output-cannot-be-empty',
          );
        }

        return true;
      },
    }),
  ],
  outwardConnectionPoints: [
    validator('has-many', {
      disabled: reads('model.isAgentic'),
    }),
  ],
  finProcedureContent: [
    validator('inline', {
      validate(value: FinCustomProcedureContent, _options: any, model: FinCustom) {
        // Validations stolen from the instructions validator below
        // Skip validation if not V3
        if (!model.isV3) {
          return true;
        }

        // Extract instruction blocks from taskStep structure
        let instructionBlocksContent: BlockList = [];
        let decideBlocks: BlockList = [];
        let runSectionBlocks: BlockList = [];
        if (value?.content) {
          for (let block of value.content) {
            if (block.type === 'taskStep') {
              let taskStep = block;
              if (taskStep.content.type === 'instructionTaskStep') {
                let instructionTaskStep = taskStep.content;
                instructionBlocksContent.push(...instructionTaskStep.content);
              } else if (taskStep.content.type === 'decideTaskStep') {
                let decideTaskStep = taskStep.content;
                decideBlocks.push(decideTaskStep);
              } else if (taskStep.content.type === 'runSectionTaskStep') {
                let runSectionTaskStep = taskStep.content;
                runSectionBlocks.push(runSectionTaskStep);
              }
            }
          }
        }

        // Filter for valid instructions by checking if blocks contain non-empty text or non-empty items arrays
        let validInstructions = instructionBlocksContent.filter((block: $TSFixMe) => {
          if (block.text !== undefined) {
            return block.text.length > 0;
          }
          if (block.items !== undefined && block.items.length > 0) {
            return block.items.join('').length > 0;
          }
          return false;
        });

        // Validate that any blocks with data-prompt-data-operation="use_action" also include data-prompt-data-subject-identifier
        let instructionsWithInvalidActions = instructionBlocksContent.filter((block: $TSFixMe) => {
          if (block.type === 'unorderedList') {
            return block.items.some(
              (item: $TSFixMe) =>
                item.includes('data-prompt-data-operation="use_action"') &&
                (!item.includes('data-prompt-data-subject-identifier') ||
                  item.includes('data-prompt-data-subject-identifier=""')),
            );
          }

          if (block.type === 'paragraph') {
            return (
              block.text.includes('data-prompt-data-operation="use_action"') &&
              (!block.text.includes('data-prompt-data-subject-identifier') ||
                block.text.includes('data-prompt-data-subject-identifier=""'))
            );
          }
          return false;
        });

        // Return false if there are instructions with invalid actions
        if (instructionsWithInvalidActions.length > 0) {
          return model.intl.t('operator.workflows.visual-builder.validations.empty-action-error');
        }

        // Return true if there are valid instructions, decide blocks, or run section blocks present
        if (
          (instructionBlocksContent.length > 0 && validInstructions.length > 0) ||
          decideBlocks.length > 0 ||
          runSectionBlocks.length > 0
        ) {
          return true;
        }

        // Return internationalized error message for blank instruction prompts when validation fails
        return model.intl.t(
          'operator.workflows.visual-builder.validations.fin-custom.instruction-blank-prompt-error',
        );
      },
      dependentKeys: ['model.finProcedureContent.content.[]'],
    }),
  ],
};

const Validations = buildValidations({
  ...finCustomStepBlockValidators,
});

export default class FinCustom extends Step.extend(Validations) {
  @tracked referencedAttributeIdentifiers: string[] | null = null;

  @service declare intl: IntlService;
  @fragmentArray('common/blocks/block', { polymorphic: true, typeKey: 'modelKey' })
  declare blocks: SyncHasMany<Block>;

  @attr('string') declare externalStepId: string;

  @fragmentArray('common/blocks/block', { polymorphic: true, typeKey: 'modelKey' })
  declare guideline: SyncHasMany<Block>;

  @attr('array') declare guidelineCategories: string[];
  @attr('boolean', { defaultValue: false }) declare backgroundExecution: boolean;

  @attr('array') declare instructions: any[];
  @fragment('operator/visual-builder/step/fin-custom/procedure-content')
  declare finProcedureContent: FinCustomProcedureContent;

  @hasMany('operator/visual-builder/attribute-descriptor', { async: false })
  declare finOutputParams: SyncHasMany<AttributeDescriptor>;
  @attr('boolean') declare allowMultiTurn: boolean;
  @attr('boolean', { defaultValue: false }) declare allowContentSearch: boolean;
  @attr('string') declare name: string | null;
  @attr('string') declare version: string;
  @attr('string', { defaultValue: 'automatic' }) declare modelRouting: string;
  @attr('string') declare actionOutputAttributeDescriptorIdentifier?: string;
  @attr({ defaultValue: () => ({}) })
  declare actionOutputAttributeDescriptorIdentifierMapping?: Record<string, string>;

  get isV3() {
    return this.version === 'v3';
  }

  static get canBeCustomerFacing(): IsCustomerFacing {
    return IsCustomerFacing.Maybe;
  }

  get isCustomerFacing() {
    // The step is deemed customer facing if it allows multi-turn tasks, i.e. the task could require input from the user.
    return this.allowMultiTurn;
  }

  get supportedChannels() {
    return SUPPORTED_CHANNELS.finCustom;
  }

  static get supportedContexts() {
    return [TargetContext.Conversation, TargetContext.CustomerTicket];
  }

  get jsonBlocks() {
    return this.blocks.serialize();
  }

  get app() {
    return this.appService.app;
  }

  get isAgentic() {
    return (
      this.app.canUseAgenticWorkflows && this.isV3 && this.outwardConnectionPoints.length === 0
    );
  }

  @computed('blocks', 'isV3', 'finProcedureContent.content.[]', 'referencedAttributeIdentifiers')
  get localVariableIdentifiersUsed() {
    if (this.referencedAttributeIdentifiers) {
      return this.referencedAttributeIdentifiers.filter((identifier) =>
        identifier.startsWith(LOCAL_VARIABLE_IDENTIFIER_PREFIX),
      );
    }

    if (this.isV3) {
      let instructionBlocks: BlockList = [];
      if (this.finProcedureContent?.content) {
        for (let block of this.finProcedureContent.content) {
          if (block.type === 'taskStep') {
            let taskStep = block;
            if (taskStep.content.type === 'instructionTaskStep') {
              let instructionTaskStep = taskStep.content;
              instructionBlocks.push(...instructionTaskStep.content);
            }
          }
        }
      }

      let templateVariables = extractTemplateVariables(instructionBlocks, true);
      return templateVariables.filter((variable) =>
        variable.startsWith(LOCAL_VARIABLE_IDENTIFIER_PREFIX),
      );
    } else {
      let templateVariables = extractTemplateVariables(this.blocks.toArray());
      return templateVariables.filter((variable) =>
        variable.startsWith(LOCAL_VARIABLE_IDENTIFIER_PREFIX),
      );
    }
  }

  get serializedBlocks() {
    // We only need to serialize the blocks if they are a FragmentArray (in tests they are already serialized)
    if (this.blocks.type === 'common/blocks/block') {
      return this.blocks.serialize();
    } else {
      return this.blocks;
    }
  }

  get serializedGuideline() {
    // We only need to serialize the blocks if they are a FragmentArray (in tests they are already serialized)
    if (this.guideline.type === 'common/blocks/block') {
      return this.guideline.serialize();
    } else {
      return this.guideline;
    }
  }

  get serializedInstructions() {
    return this.instructions.map((instruction) => instruction.serialize());
  }

  @computed(
    'hasDirtyAttributes',
    'outwardConnectionPoints.@each.hasUnsavedChanges',
    'instructions.[]',
  )
  get hasUnsavedChanges() {
    return (
      this.hasDirtyAttributes ||
      this.outwardConnectionPoints.isAny('hasUnsavedChanges') ||
      this.instructions.some((instruction) =>
        instruction.some((block: $TSFixMe) => block.hasDirtyAttributes),
      )
    );
  }

  static get requiredBillingFeature() {
    return 'workflows_core_billing_feature';
  }

  static createNewStep(store: Store, params: CreateStepParams = {}): FinCustom {
    let app = getApp();
    let version =
      (app.canUseFinTaskV3 && app.canUseFinTaskV3InAllWorkflows) ||
      (params.inFinTask && app.canUseFinTaskV3)
        ? 'v3'
        : 'v2';

    let blocks = [
      store.createFragment('common/blocks/paragraph', {
        type: 'paragraph',
        text: '', // let the component decide what the default or placeholder text should be
      }),
    ];

    let guideline = [
      store.createFragment('common/blocks/paragraph', {
        type: 'paragraph',
        text: '', // let the component decide what the default or placeholder text should be
      }),
    ];

    let instructions: $TSFixMe = [];
    if (version === 'v3') {
      instructions = [
        [
          store.createFragment('common/blocks/paragraph', {
            type: 'paragraph',
            text: '', // let the component decide what the default or placeholder text should be
          }),
        ],
        [
          store.createFragment('common/blocks/paragraph', {
            type: 'paragraph',
            text: '', // let the component decide what the default or placeholder text should be
          }),
        ],
      ];
    }

    let shouldHaveFallbackConnectionPoint =
      // all v2 tasks in both workflows and tasks should have a fallback path
      version === 'v2' ||
      // when v3 step is used in non agentic tasks should have a fallback path
      !app.canUseAgenticWorkflows ||
      // when v3 step is used in a workflow it should have a fallback path
      !params.inFinTask;

    let outwardConnectionPoints = shouldHaveFallbackConnectionPoint
      ? ([
          store.createRecord('operator/visual-builder/connection-point', { type: 'fallback' }),
        ] as ConnectionPoint[])
      : [];

    let finProcedureContent = store.createFragment(
      'operator/visual-builder/step/fin-custom/procedure-content',
      {
        content: [
          {
            type: 'taskStep',
            content: {
              type: 'instructionTaskStep',
              content: [
                {
                  type: 'paragraph',
                  text: '',
                },
              ],
            },
          },
          {
            type: 'taskStep',
            content: {
              type: 'instructionTaskStep',
              content: [
                {
                  type: 'paragraph',
                  text: '',
                },
              ],
            },
          },
        ],
      },
    );

    return store.createRecord('operator/visual-builder/step/fin-custom', {
      type: 'operator/visual-builder/step/fin-custom',
      version,
      blocks,
      guideline,
      guidelineCategories: [],
      backgroundExecution: false,
      instructions,
      outwardConnectionPoints,
      finProcedureContent,
      externalStepId: generateUUID(),
      modelRouting: 'automatic',
    });
  }
}
